import { useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight, ImagePlus } from "lucide-react";

/**
 * Generic photo slider used by the Felicitation and Sports Day sections.
 * Each slide can either provide a `src` (real image) or be left empty —
 * empty slots render a clean branded placeholder card so the section
 * structure is ready to receive real photos later.
 */
export default function PhotoSlider({
  testid,
  slides,
  accentIcon: AccentIcon,
}) {
  const autoplay = useRef(
    Autoplay({ delay: 4500, stopOnInteraction: false, stopOnMouseEnter: true })
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start" },
    [autoplay.current]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const scrollTo = (i) => emblaApi && emblaApi.scrollTo(i);
  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  return (
    <div data-testid={testid} className="relative">
      <div className="overflow-hidden rounded-md" ref={emblaRef}>
        <div className="flex">
          {slides.map((s, i) => (
            <div
              key={s.id}
              data-testid={`${testid}-slide-${i}`}
              className="min-w-0 shrink-0 grow-0 basis-full"
            >
              <SlideFrame
                src={s.src}
                label={s.label}
                AccentIcon={AccentIcon}
                eager={i === 0}
              />
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={scrollPrev}
        data-testid={`${testid}-prev`}
        aria-label="Previous"
        className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-700 shadow-md backdrop-blur transition-all hover:bg-red-600 hover:text-white md:flex"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={scrollNext}
        data-testid={`${testid}-next`}
        aria-label="Next"
        className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-700 shadow-md backdrop-blur transition-all hover:bg-red-600 hover:text-white md:flex"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <div className="mt-4 flex justify-center md:mt-6">
        <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
          {scrollSnaps.map((_, i) => (
            <button
              key={i}
              data-testid={`${testid}-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => scrollTo(i)}
              className={`h-2 rounded-full transition-all ${
                i === selectedIndex
                  ? "w-6 bg-red-600"
                  : "w-2 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function SlideFrame({ src, label, AccentIcon, eager }) {
  return (
    <div className="relative w-full">
      <div className="relative aspect-[4/5] w-full overflow-hidden rounded-md border border-red-100 bg-gradient-to-br from-red-50 via-orange-50 to-white sm:aspect-[5/4] md:aspect-[16/9]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.6),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(255,200,170,0.35),transparent_45%)]"
        />
        <span className="absolute left-3 top-3 h-6 w-6 rounded-tl-md border-l-2 border-t-2 border-red-500/70 md:left-5 md:top-5 md:h-8 md:w-8" />
        <span className="absolute right-3 top-3 h-6 w-6 rounded-tr-md border-r-2 border-t-2 border-red-500/70 md:right-5 md:top-5 md:h-8 md:w-8" />
        <span className="absolute bottom-3 left-3 h-6 w-6 rounded-bl-md border-b-2 border-l-2 border-red-500/70 md:bottom-5 md:left-5 md:h-8 md:w-8" />
        <span className="absolute bottom-3 right-3 h-6 w-6 rounded-br-md border-b-2 border-r-2 border-red-500/70 md:bottom-5 md:right-5 md:h-8 md:w-8" />

        <div className="absolute inset-0 flex items-center justify-center p-4 md:p-8">
          {src ? (
            <img
              src={src}
              alt={label || ""}
              loading={eager ? "eager" : "lazy"}
              className="max-h-full max-w-full rounded-sm object-contain shadow-xl shadow-red-900/15"
              draggable={false}
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-slate-500">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-red-100 md:h-20 md:w-20">
                {AccentIcon ? (
                  <AccentIcon
                    className="h-7 w-7 text-red-600 md:h-9 md:w-9"
                    strokeWidth={1.8}
                  />
                ) : (
                  <ImagePlus
                    className="h-7 w-7 text-red-600 md:h-9 md:w-9"
                    strokeWidth={1.8}
                  />
                )}
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-red-700 md:text-[12px]">
                Photo Coming Soon
              </span>
              {label && (
                <span className="max-w-[80%] text-center text-[12px] text-slate-500 md:text-[13px]">
                  {label}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
